import React, { useMemo, useRef, useState } from "react";
import {
  mdiAlertCircleOutline,
  mdiCheckCircleOutline,
  mdiClockOutline,
  mdiFileSearchOutline,
  mdiProgressUpload,
  mdiTrayArrowDown
} from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import Header from "components/Header";
import Modal from "components/Modal";
import StatusModal from "components/StatusModal";
import { ExclamationSVGIcon } from "components/SVGIcons";
import { useUserFeature } from "hooks/useUserFeature";
import MainLayout from "layouts/Main";
import FileUploader from "./FileUploader";
import {
  BulkUploadStatusItem,
  extractBulkUploadErrorMessages,
  formatBulkUploadMessages,
  isBulkUploadPermissionError,
  uploadBulkFundingRequestFile
} from "./bulkUpload";
import FileList from "./FileList";
import UploadStatusList from "./UploadStatusList";
import styles from "./style.module.scss";

export default function BulkUploads() {
  const { isCarrierSubscribed } = useUserFeature();
  const [isPermissionModalOpen, togglePermissionModal] = useToggle();
  const [isSuccessModalOpen, toggleSuccessModal] = useToggle();
  const [isErrorModalOpen, toggleErrorModal] = useToggle();
  const [isLoading, setIsLoading] = useState(false);
  const [loadMessage, setLoadMessage] = useState<string[]>([]);
  const [fileList, setFileList] = useState<File[]>([]);
  const [uploadItems, setUploadItems] = useState<BulkUploadStatusItem[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const addFile = (file: File) => {
    setFileList([file]);
    setUploadItems([]);
    setLoadMessage([]);
  };

  const removeFile = (fileId: number) => {
    setFileList((prev) => prev.filter((file) => file.lastModified !== fileId));
    setUploadItems([]);
    setLoadMessage([]);
  };

  const templateHref = isCarrierSubscribed
    ? "/other/template_carrier_bulk_import_example.xlsx"
    : "/other/broker_bulk_upload_template.xlsx";

  const templateDownloadName = isCarrierSubscribed
    ? "template_carrier_bulk_import_example.xlsx"
    : "broker_bulk_upload_template.xlsx";

  const bulkFundingRequest = async () => {
    if (!fileList.length) {
      return;
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      setIsLoading(true);
      setLoadMessage([]);
      setUploadItems([]);
      const response = await uploadBulkFundingRequestFile(
        fileList[0],
        setUploadItems,
        isCarrierSubscribed,
        abortController.signal
      );

      if (abortController.signal.aborted) {
        return;
      }

      const failMessages = formatBulkUploadMessages(response.fail, isCarrierSubscribed);
      const successMessages = formatBulkUploadMessages(response.success, isCarrierSubscribed);
      setLoadMessage([...failMessages, ...successMessages]);

      if (response.fail.length > 0) {
        toggleErrorModal();
      } else {
        toggleSuccessModal();
      }
    } catch (error) {
      if (isBulkUploadPermissionError(error)) {
        togglePermissionModal();
      } else {
        setLoadMessage(extractBulkUploadErrorMessages(error, isCarrierSubscribed));
        toggleErrorModal();
      }
    } finally {
      abortControllerRef.current = null;
      setIsLoading(false);
    }
  };

  const cancelBulkFundingRequest = () => {
    abortControllerRef.current?.abort();
  };

  const waitingItems = useMemo(
    () => uploadItems.filter((item) => item.status === "waiting"),
    [uploadItems]
  );
  const uploadingItems = useMemo(
    () => uploadItems.filter((item) => item.status === "uploading"),
    [uploadItems]
  );
  const successItems = useMemo(
    () => uploadItems.filter((item) => item.status === "success"),
    [uploadItems]
  );
  const errorItems = useMemo(
    () => uploadItems.filter((item) => item.status === "error"),
    [uploadItems]
  );
  const processedCount = successItems.length + errorItems.length;
  const progressPercentage = uploadItems.length
    ? Math.round((processedCount / uploadItems.length) * 100)
    : 0;
  const selectedFile = fileList[0];
  const summaryCards = [
    {
      label: "Uploading",
      value: uploadingItems.length,
      icon: mdiProgressUpload,
      toneClass: styles.summaryCardUploading
    },
    {
      label: "Waiting",
      value: waitingItems.length,
      icon: mdiClockOutline,
      toneClass: styles.summaryCardWaiting
    },
    {
      label: "Completed",
      value: successItems.length,
      icon: mdiCheckCircleOutline,
      toneClass: styles.summaryCardSuccess
    },
    {
      label: "Errors",
      value: errorItems.length,
      icon: mdiAlertCircleOutline,
      toneClass: styles.summaryCardError
    }
  ];

  return (
    <MainLayout
      desktopTitle="Bulk Upload"
      customHeader={<Header label="Bulk Upload" isHideDesktop />}
      className={clsx(styles.bulkUploadsContainer, "desktop:!mx-auto")}>
      <div className={styles.heroSection}>
        <div className={styles.heroPanel}>
          <div className={styles.heroEyebrow}>
            <span className={styles.heroEyebrowDot} />
            Bulk Upload
          </div>
          <div className={styles.heroTitleRow}>
            <div>
              <h2 className={styles.heroTitle}>Upload your funding request template</h2>
              <p className={styles.heroDescription}>
                Review your file, submit it, and check which funding requests were completed and
                which ones still need updates.
              </p>
            </div>
            <div className={styles.heroFileBadge}>
              <Icon path={mdiFileSearchOutline} size={1} />
              <span>{isCarrierSubscribed ? "Carrier upload" : "Broker upload"}</span>
            </div>
          </div>

          <div className={styles.summaryGrid}>
            {summaryCards.map((card) => (
              <div key={card.label} className={clsx(styles.summaryCard, card.toneClass)}>
                <span className={styles.summaryCardIcon}>
                  <Icon path={card.icon} size={0.9} />
                </span>
                <div className={styles.summaryCardText}>
                  <span className={styles.summaryCardValue}>{card.value}</span>
                  <span className={styles.summaryCardLabel}>{card.label}</span>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.progressPanel}>
            <div className={styles.progressHeader}>
              <span>Progress</span>
              <span>{uploadItems.length ? `${progressPercentage}%` : "Ready"}</span>
            </div>
            <div className={styles.progressTrack}>
              <div className={styles.progressFill} style={{ width: `${progressPercentage}%` }} />
            </div>
            <div className={styles.progressMeta}>
              <span>
                {uploadItems.length
                  ? `${processedCount} of ${uploadItems.length} rows reviewed`
                  : "Select a template file to begin"}
              </span>
              <span>
                {errorItems.length
                  ? `${errorItems.length} row${errorItems.length === 1 ? "" : "s"} need updates`
                  : "No updates needed so far"}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.heroSideCard}>
          <span className={styles.heroSideLabel}>Selected File</span>
          {selectedFile ? (
            <>
              <FileList files={fileList} onRemove={removeFile} />
              <p className={styles.heroSideDescription}>
                Use the results below to confirm which rows were submitted and which rows need to be
                corrected before you upload again.
              </p>
            </>
          ) : (
            <p className={styles.heroSideDescription}>
              Choose your broker or carrier template file to review the upload results here.
            </p>
          )}
          <div className={styles.heroUploaderWrap}>
            <FileUploader
              isCarrierSubscribed={isCarrierSubscribed}
              onFileSelect={addFile}
              compact
            />
          </div>
        </div>
      </div>

      {uploadItems.length ? (
        <div className={styles.statusGrid}>
          <UploadStatusList
            title="Uploading"
            items={uploadingItems}
            tone="uploading"
            caption="Rows currently being submitted"
            emptyMessage={isLoading ? "No rows are uploading right now." : undefined}
          />
          <UploadStatusList
            title="Waiting"
            items={waitingItems}
            tone="waiting"
            caption="Rows still waiting to be uploaded"
            emptyMessage={isLoading ? "No rows are waiting right now." : undefined}
          />
          <UploadStatusList
            title="Completed"
            items={successItems}
            tone="success"
            caption="Rows that were submitted successfully"
            emptyMessage={!isLoading ? "Completed rows will appear here after upload." : undefined}
          />
          <UploadStatusList
            title="Needs Updates"
            items={errorItems}
            tone="error"
            caption="Rows that need changes before you try again"
            emptyMessage={
              !isLoading ? "Rows that need updates will appear here if anything fails." : undefined
            }
          />
        </div>
      ) : null}

      <div className={styles.footer}>
        <a
          href={templateHref}
          target="_blank"
          rel="noopener noreferrer"
          download={templateDownloadName}
          className={styles.templateButton}>
          <span className={styles.templateIconWrap}>
            <Icon path={mdiTrayArrowDown} className={styles.templateIcon} />
          </span>
          <span className={styles.templateTextWrap}>
            <span className={styles.templateLabel}>Download Template</span>
            <span className={styles.templateMeta}>Excel file</span>
          </span>
        </a>
        <div className={styles.footerActions}>
          {isLoading ? (
            <Button
              type="button"
              size="lg"
              variant="outline"
              className={styles.cancelButton}
              onClick={cancelBulkFundingRequest}>
              Cancel Upload
            </Button>
          ) : null}
          <Button
            type="button"
            size="lg"
            className={styles.nextButton}
            onClick={bulkFundingRequest}
            loading={isLoading}
            disabled={isLoading || !fileList.length}>
            Launch Upload
          </Button>
        </div>
      </div>

      <Modal
        isOpen={isPermissionModalOpen}
        closeModal={togglePermissionModal}
        wrapperClass="!my-auto text-sm"
        dialogClass="desktop:!m-auto !bg-foreground !px-6 !pt-5 !pb-3 !border !border-card !overflow-y-auto gap-4"
        closeClass="hidden">
        <ExclamationSVGIcon className="self-center" />
        <span className="pt-1 font-medium text-center">
          You do not have permission to perform this action.
        </span>
        <Button onClick={togglePermissionModal} className="self-center w-20 rounded-full h-9">
          OK
        </Button>
      </Modal>

      <StatusModal
        title="Success"
        content={loadMessage.map((item) => (
          <p key={item} className="pb-2 !text-sm desktop:!text-base">
            {item}
          </p>
        ))}
        isModalOpen={isSuccessModalOpen}
        toggleModal={toggleSuccessModal}
        backdropClick={toggleSuccessModal}
      />

      <StatusModal
        title="Error"
        content={loadMessage.map((item) => (
          <p key={item} className="pb-2 !text-sm desktop:!text-base">
            {item}
          </p>
        ))}
        isModalOpen={isErrorModalOpen}
        toggleModal={toggleErrorModal}
        backdropClick={toggleErrorModal}
        isError
      />
    </MainLayout>
  );
}
